import { formatCurrency } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, LOCALE_ID, OnDestroy, OnInit, signal, ViewChild, inject } from "@angular/core";
import { ChartJsContainerComponent, rgb } from "@tylertech/chart-js";
import { ChartBindingObject, ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { Command, IconCommand } from "@tylertech/commands-module";
import { formatAbbreviatedNumber } from "@tylertech/common-components-module";
import { FileService, LoggingService } from "@tylertech/core-module";
import { ICustomViewComponent } from "@tylertech/custom-view";
import { Utilities } from "@tylertech/shared-module";
import { IChartCriteria, IChartData } from "@tylertech/tyler-api-types";
import { ChartConfigurationCustomTypesPerDataset, ChartDataCustomTypesPerDataset, ChartEvent, ChartType, LegendElement, LegendItem, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "payroll-overtime-usage-chart",
    templateUrl: "./overtimeUsageChart.tmpl.html",
    standalone: false
})
export class OvertimeUsageChartComponent implements OnDestroy, OnInit, ICustomViewComponent<ChartBindingObject>
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $fileService = inject(FileService);
    private readonly $chartService = inject(ChartService);

    private readonly destructor$ = new Subject<void>();
    readonly customDataBinding: ChartBindingObject;
    readonly commands: ReadonlyArray<Command>;
    readonly ChartTitle: string = "Overtime Usage Chart by Year";
    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;
    readonly showChart = signal(false);
    chartConfig: ChartConfigurationCustomTypesPerDataset;

    private _chartCriteria: IChartCriteria;
    ChartSubtitle: string;
    isLoaded: boolean;

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable.pipe(takeUntil(this.destructor$)).subscribe((chartId: string) =>
        {
            if (chartId !== this.chartId)
            {
                return;
            }
            this.loadChartData();
        });
        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.hasData(),
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this.customDataBinding?.chartId;
    }

    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria || this.customDataBinding?.chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise(
            () =>
            {
                if (!this.hasData())
                {
                    return this.loadChartData();
                }
                this.updateChartConfiguration();
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                    if (!this.hasData())
                    {
                        return;
                    }

                    this.updateChartConfiguration();
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService.showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    private updateChartConfiguration(): void
    {
        const chartColors = this.$chartService.getColors(3);
        const trendLineColor = this.$chartService.getTrendLineColor();
        const datasetA: IChartData = this.chartCriteria.ChartDatas[0];

        this.ChartSubtitle = datasetA.Additional && datasetA.Additional[0]
            ? datasetA.Additional[0].toString()
            : "";

        let datasetB: IChartData;
        let totals: IChartData;
        if (this.chartCriteria.ChartDatas.length === 2)
        {
            totals = this.chartCriteria.ChartDatas[1];
        }
        else
        {
            datasetB = this.chartCriteria.ChartDatas[1];
            totals = this.chartCriteria.ChartDatas[2];
        }

        const shortMonths = datasetA.Labels.map(x => x.substring(0, 3));
        const barChartData: ChartDataCustomTypesPerDataset = {
            datasets: [
                {
                    type: "bar",
                    label: `${datasetA.SetLabel} OT`,
                    data: datasetA.Data,
                    yAxisID: "barAxis",
                    backgroundColor: chartColors[0],
                    borderColor: chartColors[0],
                    hoverBackgroundColor: chartColors[0],
                    hoverBorderColor: chartColors[0],
                },
            ],
        };
        if (datasetB)
        {
            barChartData.datasets.push({
                type: "bar",
                label: `${datasetB.SetLabel} OT`,
                data: datasetB.Data,
                yAxisID: "barAxis",
                backgroundColor: chartColors[1],
                borderColor: chartColors[1],
                hoverBackgroundColor: chartColors[1],
                hoverBorderColor: chartColors[1],
            });
        }
        if (totals)
        {
            barChartData.datasets.push({
                type: "line",
                label: `${totals.SetLabel}`,
                yAxisID: "y",
                data: totals.Data,
                fill: false,
                borderColor: trendLineColor,
                backgroundColor: trendLineColor,
                pointHoverBackgroundColor: trendLineColor,
                pointHoverBorderColor: trendLineColor,
            });
        }
        this.chartConfig = {
            options: {
                responsive: true,
                plugins: {
                    legend: {
                        display: true,
                        position: "bottom",
                        labels: { boxWidth: 12 },
                        onClick: (_e: ChartEvent, legendItem: LegendItem, legend: LegendElement<ChartType>): void =>
                        {
                            const index = legendItem.datasetIndex;
                            const ci = legend.chart;
                            const meta = ci.getDatasetMeta(index);
                            const yAxisID = meta.yAxisID;
                            meta.hidden = Utilities.IsNullOrUndefined(meta.hidden) ? !ci.data.datasets[index].hidden : null;
                            let showScale = false;
                            for (let i = 0; i < (ci.data.datasets || []).length; ++i)
                            {
                                const iMeta = ci.getDatasetMeta(i);
                                if (Utilities.IsNullOrUndefined(iMeta.hidden) && iMeta.yAxisID === yAxisID)
                                {
                                    showScale = true;
                                    break;
                                }
                            }
                            ci.options.scales[yAxisID].display = showScale;
                            ci.update();
                        },
                    },
                    tooltip: {
                        callbacks: {
                            title: (tooltipItems: TooltipItem<ChartType>[]) =>
                            {
                                return datasetA.Labels[tooltipItems[0].dataIndex];
                            },
                            label: (tooltipItem: TooltipItem<ChartType>) =>
                            {
                                return formatCurrency(Number(tooltipItem.raw), this.locale, "$");
                            },
                        },
                    },
                },
                elements: {
                    line: { fill: false },
                    point: !totals
                        ? {
                            radius: 0,
                            borderWidth: 0,
                            hoverRadius: 0,
                            hoverBorderWidth: 0,
                        }
                        : undefined,
                },
                datasets: {
                    bar: {
                        categoryPercentage: 0.5,
                        barPercentage: 0.8,

                    },
                    line: {
                        tension: 0.5,
                        pointStyle: 'line',
                    },
                },
                scales: {
                    x: {
                        display: true,
                        grid: { display: true },
                        labels: shortMonths,
                    },
                    barAxis: {
                        type: "linear",
                        display: true,
                        position: "right",
                        title: {
                            display: true,
                            text: "Monthly",
                            font: { size: 16, }
                        },
                        grid: { display: true },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    },
                    y: {
                        type: "linear",
                        display: !!totals,
                        position: "left",
                        title: {
                            display: true,
                            text: "Cumulative",
                            font: { size: 16, },
                        },
                        grid: { display: false },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    },
                },
            },
            data: barChartData,
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
